// Shared SSE reader for OpenAI-compatible streaming chat completions.
// OpenRouter and the BabyAI Space both speak this protocol: lines
// prefixed with "data: " each carry a JSON payload, terminated by
// "data: [DONE]". Each chunk's choices[0].delta.content is the next
// token slice. Usage statistics arrive in the final chunk (varies
// by provider — both expose .usage when stream_options.include_usage
// is set, but we just take whatever shows up).

export interface OpenAIStreamResult {
  text: string;
  model?: string;
  finish_reason?: string;
  usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
}

export async function readOpenAIStream(
  response: Response,
  onToken: (delta: string) => void,
): Promise<OpenAIStreamResult> {
  if (!response.body) throw new Error("response has no body — can't stream");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";
  let fullText = "";
  let model: string | undefined;
  let finishReason: string | undefined;
  let usage: OpenAIStreamResult["usage"];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE is line-oriented. Process complete lines; keep partial
    // tail in the buffer for the next chunk.
    let nl: number;
    while ((nl = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line || !line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      try {
        const json = JSON.parse(payload) as {
          model?: string;
          choices?: Array<{
            delta?: { content?: string };
            finish_reason?: string | null;
          }>;
          usage?: OpenAIStreamResult["usage"];
        };
        const choice = json.choices?.[0];
        const delta = choice?.delta?.content;
        if (typeof delta === "string" && delta.length > 0) {
          fullText += delta;
          onToken(delta);
        }
        if (choice?.finish_reason) finishReason = choice.finish_reason;
        if (!model && json.model) model = json.model;
        if (json.usage) usage = json.usage;
      } catch {
        // OpenRouter occasionally emits SSE comments / keepalives that
        // aren't valid JSON. Silently skip — never bring down the
        // stream over a malformed chunk.
      }
    }
  }

  return { text: fullText, model, finish_reason: finishReason, usage };
}
